import React from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import { withRouter, WithRouterProps } from 'react-router';
import { ChildProps } from 'react-apollo';

import {
  Item2ProductModuleData,
  CoreItem2CspReviews,
} from '../schemas/rql';
import { RCToggledContent, RCAlertBox } from '@reverbdotcom/cadence/components';
import Item2CSPReviews from './item2_csp_reviews';
import { Item2ReportMismatchedProduct } from './item2_report_mismatched_product';
import { Item2ProductModuleHeader } from './item2_product_module_header';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { CSPReviewFragment } from './csp_reviews';
import { Item2CspReviewsStarRating } from './item2_csp_reviews_star_rating';
import { CspReviewsPrompt } from './csp_reviews_prompt';
import { getOffsetFromPage } from '../pagination_helpers';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import CoreClient from '../lib/core_client';
import UrlHelpers from './shared/url_helpers';

interface ExternalProps {
  listing: Item2ProductModuleData.Fragment;
}

type IProps = ChildProps<WithRouterProps & ExternalProps, CoreItem2CspReviews.Query>;

const COMPONENT_NAME = 'Item2ProductModule';
const REVIEWS_LIMIT = 5;

export const Item2ProductModuleFragment = gql`
fragment Item2ProductModuleData on Listing {
  _id
  id
  categories {
    _id
    id
    slug
    name
    rootSlug
    webLink {
      href
    }
    cmsLink {
      href
    }
    parentCategories {
      _id
      slug
    }
  }
  csp {
    _id
    id
    title
    slug
    webLink {
      path
    }
    image(input: {transform: "card_square"}) {
      source
    }
    inventory {
      newLowPrice {
        display
        amountCents
      }
      usedLowPrice {
        display
        amountCents
      }
      usedTotal
      newTotal
    }
    specs {
      productSpecs {
        type
        name
        value
        path
      }
    }
  }
}
`;

function renderSubmissionSuccess(submissionSuccess: boolean) {
  if (!submissionSuccess) { return null; }

  return (
    <div className="scaling-mtb-2 width-100">
      <RCAlertBox type="success">
        {I18n.t('discovery.cspReviewModule.thankYouForReviewing')}
      </RCAlertBox>
    </div>
  );
}

export function componentClickEvent(displayStyle, path) {
  return {
    eventName: MParticleEventName.ClickedComponent,
    componentName: COMPONENT_NAME,
    displayStyle,
    entityType: 'url',
    entityId: path,
  };
}

export function Item2ProductModule(props: IProps) {
  const inCspCtaSidebarExperiment = useExpEnabled(experiments.CSP_CTA_ON_ITEM_SIDEBAR);
  const [submissionSuccess, setSubmissionSuccess] = React.useState(false);
  const pageOffset = getOffsetFromPage(props.location?.query?.review_page, REVIEWS_LIMIT);
  const { csp, categories } = props.listing;
  if (!props.data.listing) return null;
  if (!csp) return null;

  const { reviews } = props.data?.listing?.csp;
  const headingText = inCspCtaSidebarExperiment ? 'discovery.item2Layout.productReviews' : 'discovery.item2ProductModule.productOverview';
  const reviewsHeadingText = inCspCtaSidebarExperiment ? null : I18n.t('discovery.item2Layout.productReviews');
  const reviewsSpacingClassNames = inCspCtaSidebarExperiment ? '' : 'pt-2 bdt-1 bd-color-primary';

  // Configure review prompt
  const { myReview } = reviews;
  const submitUrl = myReview ? UrlHelpers.cspUpdateReviewUrl(myReview.id) : UrlHelpers.cspReviewsUrl(csp.slug, null, null);
  const onSubmit = (data) => {
    let apiCall;

    if (myReview) {
      apiCall = CoreClient.put(submitUrl, data).then(async () => {
        await props.data.refetch();
        setSubmissionSuccess(true);
      });
    } else {
      apiCall = CoreClient.post(submitUrl, data).then(async () => {
        await props.data.refetch();
        setSubmissionSuccess(true);
      });
    }

    return apiCall;
  };

  return (
    <RCToggledContent
      heading={I18n.t(`${headingText}`)}
      id="product-overview"
      lockOpen
    >
      <div className="item2-product-module">
        {inCspCtaSidebarExperiment ?
          <>
            <div className="item2-product-reviews__header">
              <CoreLink
                to={csp.webLink?.path}
                className="item2-product-module__image-link"
                clickEvent={componentClickEvent('imageLink', csp.webLink?.path)}
              >
                <div className="item2-product-reviews__image">
                  <img src={csp.image.source} alt="" />
                </div>
              </CoreLink>
              <div className="item2-product-reviews__content">
                <h4>
                  <CoreLink
                    to={csp.webLink?.path}
                    className="item2-product-module__title"
                    clickEvent={componentClickEvent('titleLink', csp.webLink?.path)}
                  >
                    {csp.title}
                  </CoreLink>
                </h4>
                <Item2CspReviewsStarRating csp={props.data.listing?.csp}/>
              </div>
            </div>
            <div className="item2-product-reviews__prompt">
              <CspReviewsPrompt csp={props.data.listing?.csp} image={csp.image} onSubmit={onSubmit} />
            </div>
            {renderSubmissionSuccess(submissionSuccess)}
          </> :
          <Item2ProductModuleHeader
            csp={csp}
            categories={categories}
            componentName={COMPONENT_NAME}
          />
        }

        <div className={`item2-product-module__reviews ${reviewsSpacingClassNames}`}>
          <Item2CSPReviews
            heading={reviewsHeadingText}
            csp={props.data.listing?.csp}
            location={props.location}
            refetch={props.data.refetch}
            pageOffset={pageOffset}
          />
        </div>
        <div className="item2-product-module__mismatch">
          <Item2ReportMismatchedProduct listingId={props.listing.id} />
        </div>
      </div>
    </RCToggledContent>
  );
}

const withQuery = withGraphql<WithRouterProps & ExternalProps, CoreItem2CspReviews.Query>(
  gql`
    query Core_Item2_CSPReviews(
      $limit: Int
      $id: String
      $offset: Int
    ) {
      listing(
        input: {id: $id}
      ) {
        _id
        csp {
          _id
          ...CSPReviewCSP
          reviews(input: {limit: $limit, offset: $offset}) {
            ...CSPReviews
          }
        }
      }
    }
    ${CSPReviewFragment}
  `,
  {
    options: (ownProps) => {
      return {
        // allows for partial failures in RQL response

        errorPolicy: 'all',
        ssr: false,
        variables: {
          id: ownProps.params.id.split('-')[0],
          limit: REVIEWS_LIMIT,
          offset: getOffsetFromPage(ownProps.location.query.review_page, REVIEWS_LIMIT),
        },
      };
    },
  },
);

export default withRouter(withQuery(Item2ProductModule));
